"use client";

import { useEffect, useRef } from "react";
import Clarity from "@microsoft/clarity";
import { useAuthStore } from "@/stores/auth-store";

const PROJECT_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID || "w5skp0f5no";

/**
 * ClarityProvider — initialises Microsoft Clarity once on mount and
 * identifies the logged-in user whenever auth state is set.
 *
 * Placement: inside the root layout, after all providers, so the
 * Zustand auth store is populated before we identify.
 *
 * User data sent to Clarity:
 *   - customId    → user.id  (hashed by Clarity SDK before upload)
 *   - friendlyName → "FirstName LastName"
 *   - tags        → role, companyId, companyName, workspaceId, domainId
 *
 * Note: email is intentionally excluded from customId / friendlyName
 * but can be added as a tag if needed for internal support tooling.
 */
export function ClarityProvider() {
  const initialised = useRef(false);
  const identifiedUserId = useRef<string | null>(null);

  const { user, workspaceId, domainId, isAuthenticated } = useAuthStore();

  // Initialise Clarity once — only in the browser and only in production
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (initialised.current) return;
    if (process.env.NODE_ENV !== "production") return;

    Clarity.init(PROJECT_ID);
    initialised.current = true;
  }, []);

  // Identify user whenever auth state changes
  useEffect(() => {
    if (!initialised.current) return;
    if (!isAuthenticated || !user?.id) {
      identifiedUserId.current = null;
      return;
    }

    // Skip re-identifying the same user session
    if (identifiedUserId.current === user.id) return;
    identifiedUserId.current = user.id;

    const friendlyName =
      [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;

    // Identify the user — Clarity hashes customId before sending
    Clarity.identify(user.id, undefined, undefined, friendlyName);

    // Custom tags for filtering sessions in the Clarity dashboard
    Clarity.setTag("role", user.role || "unknown");
    Clarity.setTag("companyId", user.companyId || "");
    Clarity.setTag("companyName", user.companyName || "");
    if (workspaceId) Clarity.setTag("workspaceId", workspaceId);
    if (domainId) Clarity.setTag("domainId", domainId);
  }, [isAuthenticated, user, workspaceId, domainId]);

  return null;
}
